import { type ComponentPropsWithoutRef, type ElementType } from 'react';

import { cn } from '@/utils/cn';

export type StackGap = 'none' | 'xs' | 'sm' | 'md' | 'lg';

export interface StackProps extends ComponentPropsWithoutRef<'div'> {
  as?: ElementType;
  direction?: 'vertical' | 'horizontal';
  gap?: StackGap;
  align?: 'start' | 'center' | 'end' | 'stretch';
  justify?: 'start' | 'center' | 'end' | 'between';
}

const GAPS: Record<StackGap, string> = {
  none: 'gap-0',
  xs: 'gap-1',
  sm: 'gap-2',
  md: 'gap-3',
  lg: 'gap-4',
};

const ALIGN = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  stretch: 'items-stretch',
} as const;

const JUSTIFY = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
  between: 'justify-between',
} as const;

/**
 * Exists so screens never hand-roll margins, which is how inconsistent vertical rhythm creeps in —
 * and, in a right-to-left locale, how a physical `ml-` survives review (ADR-031). A gap has no
 * direction.
 */
export function Stack({
  as: Component = 'div',
  direction = 'vertical',
  gap = 'md',
  align = 'stretch',
  justify = 'start',
  className,
  ...rest
}: StackProps) {
  return (
    <Component
      className={cn(
        'flex',
        direction === 'vertical' ? 'flex-col' : 'flex-row',
        GAPS[gap],
        ALIGN[align],
        JUSTIFY[justify],
        className,
      )}
      {...rest}
    />
  );
}
